import { create } from 'zustand';

import { composeWorkflowKey, type WorkflowIdentity } from './workflowViewPrefsStore';
import type { WorkflowMode } from './workflowLayoutStore';

/**
 * In-memory persistence of the design canvas viewport (pan + zoom) across
 * component remounts *within a single session*.
 *
 * Why this exists:
 *   The Designer canvas (`WorkflowDesigner` → `<ReactFlow>`) is conditionally
 *   rendered by `WorkflowDesignerWithCode` based on the active tab. Switching
 *   to the Monitoring tab UNMOUNTS the designer subtree; switching back
 *   REMOUNTS it. On remount, `<ReactFlow fitView>` re-fits the view to all
 *   nodes, discarding whatever the user was looking at — especially jarring
 *   while the assistant is still adding nodes (issue #1675). Persisting the
 *   viewport here lets the designer restore the user's framing on remount
 *   instead of snapping to a full fit.
 *
 * Design notes:
 *   - IN-MEMORY only (no `persist` middleware). A viewport is ephemeral,
 *     high-frequency UI state; it should survive within-session remounts
 *     (tab switches) but reset on a full page reload. Persisting it to
 *     localStorage/server would be noise and could resurrect a stale view.
 *   - Keyed by the same `(user, tenant, project, app, workflow)` identity used
 *     by `workflowLayoutStore` / `workflowViewPrefsStore`, plus the workflow
 *     `mode` (draft/published) so the two branches don't clobber each other.
 *   - Only the DESIGN canvas writes here. The Monitoring view also renders a
 *     `WorkflowDesigner`, but its run-snapshot viewport must not pollute the
 *     designer's saved view — callers gate writes on `!isMonitoringMode`.
 *   - Accessed via `getState()` (imperative) rather than as a reactive hook so
 *     capturing/restoring the viewport never triggers React re-renders.
 */

export interface CanvasViewport {
  x: number;
  y: number;
  zoom: number;
}

type ModeBranch = Partial<Record<WorkflowMode, CanvasViewport>>;

interface CanvasViewportState {
  byWorkflow: Record<string, ModeBranch>;
}

interface CanvasViewportActions {
  /** Read the saved viewport for a workflow+mode, or `undefined` if none. */
  getViewport: (identity: WorkflowIdentity, mode: WorkflowMode) => CanvasViewport | undefined;
  /** Save the viewport for a workflow+mode. Non-finite values are ignored. */
  setViewport: (identity: WorkflowIdentity, mode: WorkflowMode, viewport: CanvasViewport) => void;
  /** Remove the saved viewport for a workflow+mode. */
  clearViewport: (identity: WorkflowIdentity, mode: WorkflowMode) => void;
  /** Remove all saved viewports (e.g. on sign-out). */
  clearAll: () => void;
}

type CanvasViewportStore = CanvasViewportState & CanvasViewportActions;

function isFiniteViewport(viewport: CanvasViewport): boolean {
  return (
    Number.isFinite(viewport.x) &&
    Number.isFinite(viewport.y) &&
    Number.isFinite(viewport.zoom)
  );
}

export const useCanvasViewportStore = create<CanvasViewportStore>((set, get) => ({
  byWorkflow: {},

  getViewport: (identity, mode) => get().byWorkflow[composeWorkflowKey(identity)]?.[mode],

  setViewport: (identity, mode, viewport) => {
    if (!isFiniteViewport(viewport)) return;
    const key = composeWorkflowKey(identity);
    set((state) => ({
      byWorkflow: {
        ...state.byWorkflow,
        [key]: {
          ...state.byWorkflow[key],
          [mode]: { x: viewport.x, y: viewport.y, zoom: viewport.zoom },
        },
      },
    }));
  },

  clearViewport: (identity, mode) => {
    const key = composeWorkflowKey(identity);
    set((state) => {
      const branch = state.byWorkflow[key];
      if (!branch || !(mode in branch)) return state;
      const nextBranch: ModeBranch = { ...branch };
      delete nextBranch[mode];
      const nextByWorkflow = { ...state.byWorkflow };
      if (Object.keys(nextBranch).length === 0) {
        delete nextByWorkflow[key];
      } else {
        nextByWorkflow[key] = nextBranch;
      }
      return { byWorkflow: nextByWorkflow };
    });
  },

  clearAll: () => set({ byWorkflow: {} }),
}));
